import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { CardStatusBanner } from '@/components/ItemCard/CardStatusBanner';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryFirstImageUrl,
    getItemSummaryPhotosCount,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { addImageParams } from '@/utils/urls';

import { DEFAULT_CARD_DIMENSIONS } from '../../constants/defaultCardDimensions.constant';

// Applied to both the Link and the inner div: the div gets h-full/w-full from its own base
// classes at this breakpoint, so it needs the same fixed size repeated or it would stretch to
// 100% of the Link's box instead of the intended fixed dimensions.
const RANGE_MOBILE_SIZE_CLASSES =
    'max-[560px]:h-[116px] max-[560px]:min-h-[116px] max-[560px]:w-[116px] max-[560px]:min-w-[116px] xsMax:h-[136px] xsMax:w-[136px]';

export type ItemCardImageProps = {
    alignBannerBottomWithGap?: boolean;
    className?: string;
    /** Both the height and width of image */
    dimension?: number;
    imageQuality?: number;
    itemId: number;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onNavigate?: () => void;
    sharpenImage?: boolean;
    shouldGiveRangeForMobileSize?: boolean;
    showBidStatus?: boolean;
    useInFluidGrid?: boolean;
};

export default function ItemCardImage({
    alignBannerBottomWithGap = false,
    className,
    dimension,
    imageQuality,
    itemId,
    onMouseEnter,
    onMouseLeave,
    onNavigate,
    sharpenImage,
    shouldGiveRangeForMobileSize,
    showBidStatus = false,
    useInFluidGrid = false,
}: ItemCardImageProps) {
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const itemUrl = useItemOrPriceResultLink(itemId, true);
    const numPhotos = useAppSelector((state) => getItemSummaryPhotosCount(state, itemId));
    const firstPhoto = useAppSelector((state) => getItemSummaryFirstImageUrl(state, itemId));
    const imageUrl = addImageParams({
        height: dimension ? dimension : DEFAULT_CARD_DIMENSIONS.desktop,
        quality: imageQuality,
        sharpen: sharpenImage,
        url: firstPhoto,
    });

    return (
        <Link
            className={cn(
                'block h-full w-full cursor-pointer',
                shouldGiveRangeForMobileSize && RANGE_MOBILE_SIZE_CLASSES,
                className
            )}
            data-testid="itemCardImageLink"
            href={itemUrl}
            onClick={() => {
                onNavigate?.();
            }}
        >
            <div
                aria-label="Item image"
                className={cn(
                    'flex h-full w-full items-center justify-center xsMax:h-full xsMax:w-full',
                    shouldGiveRangeForMobileSize && RANGE_MOBILE_SIZE_CLASSES,
                    useInFluidGrid && 'xsMax:flex-col-reverse xsMax:justify-between'
                )}
                data-testid="itemCardImage"
            >
                {showBidStatus && (
                    <CardStatusBanner
                        alignBannerBottomWithGap={alignBannerBottomWithGap}
                        alignBannerToBottom={!alignBannerBottomWithGap}
                        itemId={itemId}
                    />
                )}
                <img
                    alt={`${title} (1 of ${numPhotos})`}
                    className={cn(
                        'h-full max-h-full w-full max-w-full object-contain',
                        useInFluidGrid && 'absolute inset-0 m-auto'
                    )}
                    height={dimension ? dimension : DEFAULT_CARD_DIMENSIONS.desktop}
                    loading="lazy"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    src={imageUrl}
                    width={dimension ? dimension : DEFAULT_CARD_DIMENSIONS.desktop}
                />
            </div>
        </Link>
    );
}
